import { useCallback, useEffect, useState } from "react";
import { LogIn, LogOut, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { getTodaysTimesheet, clockInToday, clockOutToday } from "@/lib/api";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");
const hoursBetween = (a, b) => Math.max(0, (new Date(b) - new Date(a)) / 36e5);

// Tech clock pill — manual Clock In / Clock Out (always available). GPS keeps auto-logging alongside;
// the PM Day Review compares the two.
export default function ClockPill({ techId, readOnly = false }) {
  const { t } = useI18n();
  const [ts, setTs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);

  const load = useCallback(async () => {
    if (!techId) return;
    try { const r = await getTodaysTimesheet(techId); setTs(r.data?.timesheet || null); } catch { /* keep last */ }
  }, [techId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(() => tick((x) => x + 1), 60_000); return () => clearInterval(id); }, []);

  if (!techId) return null;
  const punches = ts?.manual_punches || [];
  const open = punches.length ? punches[punches.length - 1] : null;
  const isIn = !!(open && !open.out_at);
  const closedHours = punches.filter((p) => p.in_at && p.out_at).reduce((a, p) => a + hoursBetween(p.in_at, p.out_at), 0);
  const liveHours = closedHours + (isIn ? hoursBetween(open.in_at, new Date().toISOString()) : 0);
  const locked = ["approved", "ready_to_push", "pushed"].includes(ts?.status);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn(techId);
      setTs((cur) => ({ ...(cur || {}), ...r.data }));
      toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Could not update the clock"));
    } finally { setBusy(false); }
  };

  return (
    <div className={`inline-flex h-8 items-center gap-1.5 rounded-full border pl-2.5 pr-1 text-xs ${isIn ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600"}`}
      data-testid="clock-pill" data-state={isIn ? "in" : "out"}>
      <Clock className="h-3.5 w-3.5 shrink-0" />
      <span className="whitespace-nowrap num" data-testid="clock-pill-status">
        {isIn
          ? `${t("Clocked in {time}", { time: fmtTime(open.in_at) })} · ${liveHours.toFixed(1)}h`
          : punches.length
            ? `${t("Clocked out {time}", { time: fmtTime(open.out_at) })} · ${liveHours.toFixed(1)}h`
            : t("Not clocked in")}
      </span>
      {locked && <span className="text-[10px] text-slate-400 pr-1.5" data-testid="clock-pill-locked">{t("Locked — timesheet approved")}</span>}
      {!readOnly && !locked && (
        isIn ? (
          <button type="button" onClick={() => act(clockOutToday, t("Clocked out"))} disabled={busy}
            className="inline-flex h-6 items-center gap-1 rounded-full bg-slate-900 px-2.5 font-medium text-white hover:bg-slate-700 disabled:opacity-60 dark:bg-white dark:text-slate-900"
            data-testid="btn-clock-out">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />} {t("Clock Out")}
          </button>
        ) : (
          <button type="button" onClick={() => act(clockInToday, t("Clocked in"))} disabled={busy}
            className="inline-flex h-6 items-center gap-1 rounded-full bg-emerald-600 px-2.5 font-medium text-white hover:bg-emerald-500 disabled:opacity-60"
            data-testid="btn-clock-in">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogIn className="h-3 w-3" />} {t("Clock In")}
          </button>
        )
      )}
    </div>
  );
}
